import {
  BILLING_CATALOG,
  BILLING_INTERVALS,
  BILLING_TRIAL,
  BASE_PRODUCT_IDS,
  BILLING_EXTRA_MINUTES,
} from '../../constants/billingCatalog';

export const formatBillingPrice = (price, { suffix = '' } = {}) => {
  if (!price?.amount) {
    return '—';
  }

  const value = price.amount / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (price.currency || 'eur').toUpperCase(),
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);

  return suffix ? `${formatted}${suffix}` : formatted;
};

export const getYearlySavingsPercent = (monthlyPrice, yearlyPrice) => {
  if (!monthlyPrice?.amount || !yearlyPrice?.amount) {
    return null;
  }

  const monthlyTotal = monthlyPrice.amount * 12;
  if (monthlyTotal <= 0) {
    return null;
  }

  const savings = Math.round(((monthlyTotal - yearlyPrice.amount) / monthlyTotal) * 100);
  return savings > 0 ? savings : null;
};

export const mergeCatalogWithStripePrices = (stripePrices = {}) => (
  BILLING_CATALOG.map((product) => ({
    ...product,
    prices: {
      [BILLING_INTERVALS.MONTH]: stripePrices?.[product.id]?.month ?? null,
      [BILLING_INTERVALS.YEAR]: stripePrices?.[product.id]?.year ?? null,
    },
  }))
);

export const mergeTrialWithStripePrice = (stripePrices = {}) => ({
  ...BILLING_TRIAL,
  price: stripePrices?.[BILLING_TRIAL.id]?.trial ?? null,
});

export const mergeExtraMinutesWithStripePrices = (stripePrices = {}) => (
  BILLING_EXTRA_MINUTES.map((product) => ({
    ...product,
    price: stripePrices?.[product.id]?.oneTime ?? null,
  }))
);

/** Whether the profile's active plan includes the AI Voice Agent. */
export const planHasVoiceAI = (profile) => {
  const accessStatuses = ['active', 'canceled', 'over_limit'];
  if (!profile?.subscription_plan || !accessStatuses.includes(profile.subscription_status)) {
    return false;
  }
  const plan = profile.subscription_plan.toLowerCase();
  return ['essential-and-voice', 'pro-and-voice', 'trial'].includes(plan);
};

/**
 * Active SmartWatcher catalog plans from the user profile.
 */
export const getActiveBaseProductIds = (profile) => {
  const accessStatuses = ['active', 'canceled', 'over_limit'];
  if (!profile?.subscription_plan?.trim() || !accessStatuses.includes(profile.subscription_status)) {
    return [];
  }

  const plan = profile.subscription_plan.toLowerCase();
  return BASE_PRODUCT_IDS.includes(plan) ? [plan] : [];
};

export const getIntervalLabel = (interval) => (
  interval === BILLING_INTERVALS.YEAR ? 'year' : 'month'
);

export const formatCatalogNotice = ({ warnings = [], errorMessage = '' } = {}) => {
  const raw = errorMessage || warnings.join(' ');

  if (!raw) {
    return '';
  }

  if (raw.includes('Invalid API Key') || raw.includes('Stripe secret key')) {
    return 'Could not load prices from Stripe. Set STRIPE_CATALOG_SECRET_KEY in Supabase to the Secret key from your new Stripe sandbox (starts with sk_test_ or sk_live_). Do not use publishable keys (pk_) or other key types.';
  }

  if (raw.includes('STRIPE_CATALOG_SECRET_KEY is not set')) {
    return 'Stripe catalog is not configured. Add STRIPE_CATALOG_SECRET_KEY to Supabase Edge Function secrets.';
  }

  if (warnings.length > 1 && warnings.every((item) => item.includes('Invalid API Key'))) {
    return 'Could not load prices from Stripe. Check STRIPE_CATALOG_SECRET_KEY — it must be the Secret key (sk_test_... or sk_live_...) from your new Stripe sandbox.';
  }

  return raw;
};

export const hasCatalogPrices = (products = []) => (
  products.some((product) => (
    product?.prices?.[BILLING_INTERVALS.MONTH]?.amount ||
    product?.prices?.[BILLING_INTERVALS.YEAR]?.amount
  ))
);

/** User already redeemed the one-time catalog trial. */
export const hasUsedCatalogTrial = (profile) => (
  Boolean(profile?.catalog_trial_used_at) ||
  Boolean(profile?.trial_ends_at) ||
  profile?.subscription_plan?.trim().toLowerCase() === 'trial'
);

/** User has (or had) a paid subscription plan — anything other than the trial. */
export const hasPurchasedPaidPlan = (profile) => {
  const plan = profile?.subscription_plan?.trim().toLowerCase();
  if (Boolean(profile?.stripe_subscription_id) && plan && plan !== 'trial') {
    return true;
  }
  return Boolean(plan) && plan !== 'trial';
};

/** Whether the one-time trial offer should still be shown. */
export const shouldShowTrialOffer = (profile) => (
  !hasUsedCatalogTrial(profile) && !hasPurchasedPaidPlan(profile)
);
